// React import
import React, { useState, useRef } from 'react';

// MUI imports
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { grey, red, green } from '@mui/material/colors';
import { useTheme, alpha } from '@mui/material/styles';

// Font Awesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { faLinkSlash, faMagnifyingGlassDollar, faCircleDollarToSlot } from '@fortawesome/pro-light-svg-icons';

// Generic imports
import _ from 'lodash';
import { useDrop } from 'react-dnd';
import CurrencyList from 'currency-list';

// Local imports
import OpportunityOfferTextField from './OpportunityOneOffBuilder.fieldcell.js';
import FieldSettingsDialog from './OpportunityOneOffEmailBuilder.fielddialog.js';
import { getTotalRates, getFieldRatesForProduct, ratesToText } from '../utils/rates.js';
import { findMatchingProducts } from '../utils/product.js';
import { calcFieldValue } from '../../utils/opportunity.js';
import currencyToText from '../../CurrencyToText';
import { FieldTypes } from '../../FieldTypes.js';

// Monada imports
import SupplierLogos from '@monada-ai/monada-shipping-companies-logos';

function OpportunityOfferFieldRow(props) {
    const { field, path, products, locked, offers, setOffers, setModal, errors,
        onDelete, onShowRates, onAddBuyingRate, transportationMethod, sections,
        organization, disabled, buyingRates, setBuyingRates, suppliers, exchangeRates, lockedExchangeRates, onOpenDisplayCurrencySettings } = props;
    const theme = useTheme();
    const [ dropOver, setDropOver ] = useState(null);
    const [ isEditingFieldTitle, setIsEditingFieldTitle ] = useState(false);
    const [ openFieldSettingsDialog, setOpenFieldSettingsDialog ] = useState(false);
    const [ editingFieldTitleValue, setEditingFieldTitleValue ] = useState('');
    const first = useRef();

    const setDropOverRef = useRef();
    const onAddBuyingRateRef = useRef();
    const productsRef = useRef();
    setDropOverRef.current = setDropOver;
    onAddBuyingRateRef.current = onAddBuyingRate;
    productsRef.current = products;

    const fieldBuyingRates = _.get(offers, `${path}.buyingRates`) || [];
    const buyingRateSuppliers = _(fieldBuyingRates).map(fb => buyingRates.find(rates => rates.id === fb.rateId)).compact().map(r => r.supplier.organization).uniq().value();
    
    // Group buying rates by supplier and get cost text for each
    const supplierRatesData = buyingRateSuppliers.map(supplier => {
        const supplierFieldBuyingRates = fieldBuyingRates.filter(fb => {
            const rate = buyingRates.find(r => r.id === fb.rateId);
            return rate && rate.supplier.organization === supplier;
        });
        
        const allRates = _.flatten(products.map(product => 
            getFieldRatesForProduct({ 
                fieldBuyingRates: supplierFieldBuyingRates, 
                product, 
                buyingRates, 
                transportationMethod, 
                allProducts: products, 
                organization 
            })
        )).filter(Boolean);
        
        const ratesText = allRates.length > 0 ? ratesToText({ rates: allRates, transportationMethod, exchangeRates, organization }) : [];
        
        return { supplier, ratesText };
    });

    let displayCurrency = field.displayCurrency || field.displayCurrencySource;
    if (_.isObject(displayCurrency)) {
        if (displayCurrency.convert) {
            displayCurrency = displayCurrency.to;
        } else {
            displayCurrency = null;
        }
    }

    const totalBuyingRate = getTotalRates({ field: { ...field, displayCurrency }, fieldBuyingRates, products, buyingRates, transportationMethod, exchangeRates, organization });
    const totalSellingRate = calcFieldValue({ field: { ...field, displayCurrency }, products, buyingRates, fieldBuyingRates, transportationMethod, sections, exchangeRates, organization, lockedExchangeRates })?.sum ?? {};

    const defaultCurrency = (_.keys(totalSellingRate) || [organization.defaults?.currency])[0] || organization.defaults?.currency;
    const totalSellingRateCombined = _.sum(_.map(totalSellingRate, (v, k) => v / exchangeRates[k]));

    const profit = (totalSellingRateCombined - totalBuyingRate) * exchangeRates[defaultCurrency];
    const margin = totalSellingRateCombined ? (profit / (totalSellingRateCombined * exchangeRates[defaultCurrency])) * 100 : 0;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['RateCardSingleRate', 'RateCardSingleSection', 'RateCard'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            setDropOverRef.current(monitor.canDrop(item, monitor) ? 'candrop' + (item.rate.type === 'tariff' ? '-tariff' : '') : 'cantdrop' + (item.rate.type === 'tariff' ? '-tariff' : ''));
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    setDropOverRef.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: async (item, monitor) => {
            const fields = item.section ? item.section.offers[0].fields : 
                item.field ? [item.field] :
                    item.rate.offer.sections.map(s => s.offers[0].fields.map(f => ({ ...f, title: `${s.title} / ${f.title}` }))).flat();

            for (const f of fields) {
                if (f.type === 'string') continue;
                await onAddBuyingRateRef.current({ rate: item.rate, rateFieldId: f.id, opportunityFieldId: field.id });
            }
        },
        canDrop: (item, monitor) => {
            return !_.isEmpty(findMatchingProducts({ 
                product: item.rate.product, 
                products: productsRef.current
            }));
        },
    }));

    return (
        <>
        <TableRow sx={{ position: 'relative', height: '1px' }} ref={drop}>
            {!locked && (
                <Tooltip title="delete sell rate line">
                    <IconButton
                        onClick={onDelete}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            right: '4px',
                            padding: '4px',
                            minWidth: '24px',
                            width: '24px',
                            height: '24px',
                            zIndex: 10,
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                color: theme.palette.error.main,
                                backgroundColor: alpha(theme.palette.error.main, 0.1)
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px' }} />
                    </IconButton>
                </Tooltip>
            )}
            <TableCell sx={{ verticalAlign: 'middle', height: 'inherit', padding: '4px 4px 4px 8px', borderBottom: '1px solid rgba(0, 0, 0, 0.08)', maxWidth: '280px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ flex: 0, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {isEditingFieldTitle && !locked ? (
                            <TextField
                                autoFocus
                                value={editingFieldTitleValue}
                                onChange={(e) => setEditingFieldTitleValue(e.target.value)}
                                onBlur={() => {
                                    const newOffers = _.cloneDeep(offers);
                                    _.set(newOffers, `${path}.title`, editingFieldTitleValue);
                                    setOffers(newOffers);
                                    setIsEditingFieldTitle(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.target.blur();
                                    } else if (e.key === 'Escape') {
                                        setEditingFieldTitleValue(field.title || '');
                                        setIsEditingFieldTitle(false);
                                    }
                                }}
                                variant='standard'
                                sx={{ flex: 0, minWidth: '150px' }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#0f172a',
                                    cursor: locked ? 'default' : 'pointer',
                                    '&:hover': locked ? {} : { textDecoration: 'underline' }
                                }}
                                onClick={() => {
                                    if (!locked) {
                                        setEditingFieldTitleValue(field.title || '');
                                        setIsEditingFieldTitle(true);
                                    }
                                }}
                            >
                                {field.title || <i>New field</i>}
                            </Box>
                        )}
                        <FormControl variant='standard' size='small' sx={{ minWidth: 120 }}>
                            <Select
                                disabled={disabled || locked}
                                value={field.type}
                                onChange={e => {
                                    const newType = e.target.value;
                                    const newOffers = _.cloneDeep(offers);
                                    _.set(newOffers, `${path}.type`, newType);

                                    // A field keeps its money under the key for how it is priced:
                                    // 'flat' while it is per shipment, the product's id once it is
                                    // per type. Switching moves which key gets read while the money
                                    // stays under the old one, so the new key reads empty and its
                                    // currency falls back to the organisation's own — turning a cost
                                    // loaded in GBP into AUD with nobody having touched it. Carry the
                                    // currency onto the keys the new pricing will read, asking the
                                    // field type itself which those are rather than guessing.
                                    const currentCurrency = _.values(field.values || {}).find(v => v?.currency)?.currency;
                                    const definition = FieldTypes[transportationMethod]?.find(t => t.value === newType);
                                    if (currentCurrency && definition?.getChargeLines) {
                                        let lines = [];
                                        try {
                                            lines = definition.getChargeLines({
                                                field: { ...field, type: newType },
                                                products, buyingRates: [], fieldBuyingRates: [], organization,
                                            }) || [];
                                        } catch (err) {
                                            // Only the currency is at stake here; a field type that
                                            // cannot describe itself without cargo must not take the
                                            // switch down with it.
                                            lines = [];
                                        }
                                        for (const line of lines) {
                                            if (!line?.fieldValueId) continue;
                                            const path2 = `${path}.values.${line.fieldValueId}`;
                                            const existing = _.get(newOffers, path2);
                                            // Only seed a currency where there is none. An amount
                                            // already entered against this key keeps the currency it
                                            // was entered in.
                                            if (!existing?.currency) _.set(newOffers, path2, { ...(existing || {}), currency: currentCurrency });
                                        }
                                    }
                                    setOffers(newOffers);
                                }}
                                sx={{
                                    height: '28px',
                                    '& .MuiSelect-select': {
                                        fontSize: '11px',
                                        padding: '4px 12px',
                                        height: '28px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                fontSize: '11px',
                                                padding: '4px 12px',
                                            },
                                        },
                                    },
                                }}
                            >
                                {FieldTypes[transportationMethod].map(t => (_.isFunction(t.enabled) && !t.enabled({ organization })) ? null : <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                    {errors?.find(e => e.endsWith(':type')) && (
                        <Alert severity='error'>
                            Please select the type of this field
                        </Alert>
                    )}
                    {field.type !== 'string' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '4px' }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: 500, color: profit >= 0 ? green[500] : red[500], whiteSpace: 'nowrap' }}>
                                Profit {currencyToText(defaultCurrency, true, profit.toLocaleString({ maximumFractionDigits: 2 }))} · margin {margin.toLocaleString({ maximumFractionDigits: 2 })}%
                            </Typography>
                            <Box>
                                <Tooltip title={
                                    (field.displayCurrency?.convert || field.displayCurrency?.length === 3)
                                        ? `${CurrencyList.getAll('en_US')[field.displayCurrency?.to || field.displayCurrency].name} (${field.displayCurrency?.source || field.displayCurrencySource})`
                                        : 'Display currency'
                                }>
                                    <span>
                                        <Button
                                            variant='text'
                                            sx={{
                                                minWidth: '32px',
                                                width: '32px',
                                                height: '32px',
                                                padding: '0px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                color: (field.displayCurrency?.convert || field.displayCurrency?.length === 3) ? theme.palette.primary.main : theme.palette.text.secondary,
                                                '&:hover': {
                                                    backgroundColor: (field.displayCurrency?.convert || field.displayCurrency?.length === 3) ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.text.secondary, 0.08),
                                                    color: (field.displayCurrency?.convert || field.displayCurrency?.length === 3) ? theme.palette.primary.main : theme.palette.text.secondary,
                                                    border: 'none'
                                                },
                                                '&:focus': {
                                                    outline: 'none',
                                                    border: 'none'
                                                }
                                            }}
                                            onClick={() => onOpenDisplayCurrencySettings()}
                                        >
                                            <FontAwesomeIcon icon={faCircleDollarToSlot} style={{ fontSize: '13px' }} />
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                    )}
                </Box>
            </TableCell>
            <TableCell sx={{ 
                verticalAlign: 'middle', 
                padding: '4px 8px 4px 4px', 
                textAlign: 'left',
                ...(field.type === 'string' ? { 
                    width: '100%',
                    '& > div': {
                        width: '100%'
                    }
                } : {})
            }}>
                <OpportunityOfferTextField
                    field={field} 
                    path={path}
                    products={products} 
                    defaultCurrency={organization.defaults?.currency}
                    fieldBuyingRates={fieldBuyingRates}
                    buyingRates={buyingRates}
                    transportationMethod={transportationMethod}
                    disabled={disabled || locked}
                    setField={f => {
                        setOffers(_offers => {
                            const newOffers = _.cloneDeep(_offers);
                            if (_.isFunction(f)) {
                                _.set(newOffers, path, f(_.get(newOffers, path)));
                            } else {
                                _.set(newOffers, path, f);
                            }
                            return newOffers;
                        });
                    }}
                    organization={organization}
                    sections={sections}
                    onFieldSettings={() => setOpenFieldSettingsDialog(true)}
                    exchangeRates={exchangeRates}
                    lockedExchangeRates={lockedExchangeRates}
                    buyingRateSuppliers={buyingRateSuppliers}
                    onShowRates={onShowRates}
                    fieldId={field.id}
                    supplierRatesData={supplierRatesData}
                    profit={field.type !== 'string' ? profit : undefined}
                    margin={field.type !== 'string' ? margin : undefined}
                    calculatedDefaultCurrency={field.type !== 'string' ? defaultCurrency : undefined}
                />
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top', padding: '4px 8px' }}>
                {dropOver === 'candrop' && (
                    <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                        Drop your rate here to add it to the buying rates.
                    </Box>
                )}
                {dropOver === 'candrop-tariff' && (
                    <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                        Drop your tariff here to set the price.
                    </Box>
                )}
                {dropOver === 'cantdrop' && (
                    <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                        Rate is not for a product included in the opportunity, or field types do not match.
                    </Box>
                )}
                {dropOver === 'cantdrop-tariff' && (
                    <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                        Tariff is not for a product included in the opportunity, or field types do not match.
                    </Box>
                )}
            </TableCell>
        </TableRow>
        {openFieldSettingsDialog && (
            <FieldSettingsDialog
                open={openFieldSettingsDialog}
                initialCategory="buying"
                offers={offers}
                onClose={() => setOpenFieldSettingsDialog(false)}
                field={field}
                buyingRates={buyingRates}
                onDone={({ field: updatedField, buyingRates: updatedBuyingRates }) => {
                    setOffers(_offers => {
                        const newOffers = _.cloneDeep(_offers);
                        _.set(newOffers, path, updatedField);
                        return newOffers;
                    });
                    if (setBuyingRates) setBuyingRates(updatedBuyingRates);
                    setOpenFieldSettingsDialog(false);
                }}
                products={products}
                sections={sections}
                transportationMethod={transportationMethod}
                lockedExchangeRates={lockedExchangeRates}
                organization={organization}
                suppliers={suppliers}
                exchangeRates={exchangeRates}
                onDeleteField={onDelete}
            />
        )}
        </>
    )
}

export default OpportunityOfferFieldRow;
